import {computed} from 'vue';
import type {RouteLocationAsPath} from 'vue-router';
import {useRequestURL, useRuntimeConfig} from '#imports';
import type {LocalePrefixConfig} from '../types';

type PathOrRouteLike = string | RouteLocationAsPath;

function isAbsoluteUrl(url: string): boolean {
    return /^(https?:)?\/\//i.test(url);
}

/**
 * Composable for locale prefix path manipulation on multilocale hosts (www.malt.com).
 *
 * Typical use:
 *
 * <pre>
 * const {withLocalePrefix, withoutLocalePrefix} = useLocalePrefix();
 *
 * const localizedPath = withLocalePrefix('/c/freelancers');
 * const barePath = withoutLocalePrefix('/en-gb/c/freelancers');
 * </pre>
 */
export function useLocalePrefix() {
    const runtimeConfig = useRuntimeConfig();
    const requestUrl = useRequestURL();
    const supportedLocalePrefixes = (runtimeConfig.public['@malt/nuxt-i18n-module']?.supportedLocalePrefixes ?? []) as LocalePrefixConfig[];
    const isMultiLocaleHost = computed(() => requestUrl.hostname.endsWith('.malt.com'));

    /**
     * Gets the locale prefix from a path, or from the current request URL when no path is given.
     *
     * @param path - Optional path to inspect. When omitted, uses the current request URL (multilocale hosts only).
     * @returns The locale prefix string (e.g., 'en-gb', 'en-ae') or undefined if not found
     *
     * @example
     * // On www.malt.com/en-gb/c/freelancers with supported prefix 'en-gb'
     * const prefix = getLocalePrefix(); // Returns 'en-gb'
     *
     * // On www.malt.com/c/freelancers
     * const prefix = getLocalePrefix(); // Returns undefined
     *
     * const prefix = getLocalePrefix('/en-gb/profile'); // Returns 'en-gb' on any host
     */
    function getLocalePrefix(path?: string): string | undefined {
        if (path === undefined) {
            if (!isMultiLocaleHost.value) {
                return undefined;
            }
            path = requestUrl.pathname;
        }

        const maybePrefix = path.startsWith('/') ? path.substring(1) : path;

        return supportedLocalePrefixes.find(({prefix}) => maybePrefix === prefix || maybePrefix.startsWith(`${prefix}/`))?.prefix;
    }

    /**
     * Gets the locale code corresponding to a locale prefix.
     *
     * @param localePrefix - The locale prefix to look up (e.g., 'en-gb', 'en-nl')
     * @returns The Malt locale code (e.g., 'en-GB', 'en-NL') or undefined if not found
     *
     * @example
     * const code = getLocaleCodeFromPrefix('en-gb'); // Returns 'en-GB'
     */
    function getLocaleCodeFromPrefix(localePrefix: string): string | undefined {
        return supportedLocalePrefixes.find(({prefix}) => prefix === localePrefix)?.code;
    }

    /**
     * Checks if a path or route already has a locale prefix.
     *
     * @param pathOrRoute - The path string or route object to check
     * @returns True if the path already starts with a supported locale prefix
     *
     * @example
     * const hasPrefix = hasLocalePrefix('/en-gb/c/freelancers'); // Returns true
     * const noPrefix = hasLocalePrefix('/c/freelancers'); // Returns false
     * const routeHasPrefix = hasLocalePrefix({path: '/en-ae/profile'}); // Returns true
     */
    function hasLocalePrefix(pathOrRoute: PathOrRouteLike): boolean {
        const path = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute.path;

        return getLocalePrefix(path) !== undefined;
    }

    /**
     * Prepends a locale prefix to a path or route object.
     * If the path already has a locale prefix, it won't add another one.
     * Only adds a prefix on a multilocale host (www.malt.com) when the locale prefix strategy is enabled;
     * otherwise returns the input unchanged.
     * Absolute (`http(s):`) and protocol-relative (`//`) URLs are left unchanged.
     *
     * @param pathOrRoute - The path string or route object to prefix
     * @param pathPrefix - Optional locale prefix to prepend (e.g., 'en-gb', 'en-ae'). Defaults to getLocalePrefix() when omitted
     * @returns The same type as `pathOrRoute`, with a locale prefix applied when applicable
     *
     * @example
     * const path = withLocalePrefix('/c/freelancers', 'en-gb'); // Returns '/en-gb/c/freelancers' on www.malt.com, '/c/freelancers' on malt.fr
     * const route = withLocalePrefix({path: '/c/freelancers', query: {id: '1'}}, 'en-gb');
     * // Returns {path: '/en-gb/c/freelancers', query: {id: '1'}} on www.malt.com
     * const alreadyPrefixed = withLocalePrefix('/en-gb/c/freelancers', 'en-gb'); // Returns '/en-gb/c/freelancers' (no duplicate)
     * const autoPrefix = withLocalePrefix('/c/freelancers'); // Uses locale prefix from the current URL when present
     * const absolute = withLocalePrefix('https://www.malt.fr/c/freelancers', 'en-gb'); // Returns 'https://www.malt.fr/c/freelancers'
     */
    function withLocalePrefix<T extends PathOrRouteLike>(pathOrRoute: T, pathPrefix?: string): T {
        const config = runtimeConfig.public['@malt/nuxt-i18n-module'];

        // Only add prefix on multilocale hosts and if locale strategy is enabled
        if (!isMultiLocaleHost.value || !config?.enableLocalePrefixStrategy) {
            return pathOrRoute;
        }

        // Check if already has a locale prefix
        if (hasLocalePrefix(pathOrRoute)) {
            return pathOrRoute;
        }

        // If no pathPrefix provided, get it from the current URL
        const prefix = pathPrefix ?? getLocalePrefix();

        // If we still don't have a prefix, return the path as-is
        if (!prefix) {
            return pathOrRoute;
        }

        const rawPath = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute.path;
        if (isAbsoluteUrl(rawPath)) {
            return pathOrRoute;
        }

        if (typeof pathOrRoute === 'string') {
            // Handle string path
            const path = pathOrRoute.startsWith('/') ? pathOrRoute : `/${pathOrRoute}`;
            return `/${prefix}${path}` as T;
        }

        // Handle route object
        const path = pathOrRoute.path.startsWith('/') ? pathOrRoute.path : `/${pathOrRoute.path}`;
        return {...(pathOrRoute as RouteLocationAsPath), path: `/${prefix}${path}`} as T;
    }

    /**
     * Removes a locale prefix from a path or route object if one exists.
     * Simply removes the first slug from the path if it's a locale prefix.
     *
     * @param pathOrRoute - The path string or route object to remove the prefix from
     * @returns The path or route object without the locale prefix
     *
     * @example
     * const path = withoutLocalePrefix('/en-ae/c/freelancers'); // Returns '/c/freelancers'
     * const route = withoutLocalePrefix({path: '/en-gb/profile', query: {id: '1'}});
     * // Returns {path: '/profile', query: {id: '1'}}
     * const noPrefix = withoutLocalePrefix('/c/freelancers'); // Returns '/c/freelancers' (no change)
     */
    function withoutLocalePrefix<T extends PathOrRouteLike>(pathOrRoute: T): T {
        // If no locale prefix, return as-is
        if (!hasLocalePrefix(pathOrRoute)) {
            return pathOrRoute;
        }

        const path = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute.path;
        const parts = path.split('/').filter(Boolean);
        const pathWithoutPrefix = '/' + parts.slice(1).join('/') || '/';

        if (typeof pathOrRoute === 'string') {
            return pathWithoutPrefix as T;
        } else {
            return {...(pathOrRoute as RouteLocationAsPath), path: pathWithoutPrefix} as T;
        }
    }

    return {
        getLocaleCodeFromPrefix,
        getLocalePrefix,
        hasLocalePrefix,
        withLocalePrefix,
        withoutLocalePrefix,
    };
}
